import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { NavItem } from '../api/alpha';
import { EDP_FALLBACK_NAV } from '../experience/navigation';
import { rankNavForCommand } from '../shell/navConfig';
import { getFavoritePaths, getRecentPaths } from '../shell/shellPrefs';

type Props = {
  items?: NavItem[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Command palette — Ctrl/Cmd+K. Pages, navigation, recent, favorites.
 * AI slot reserved (no AI implementation).
 */
export function CommandPalette({ items = EDP_FALLBACK_NAV, open: controlledOpen, onOpenChange }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (v: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof v === 'function' ? v(open) : v;
      if (controlledOpen === undefined) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange, open],
  );
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  const filtered = useMemo(() => rankNavForCommand(items, query), [items, query]);

  const recentItems = useMemo(() => {
    const paths = getRecentPaths();
    const map = new Map(items.map((i) => [i.path, i]));
    return paths.map((p) => map.get(p)).filter((x): x is NavItem => Boolean(x)).slice(0, 5);
  }, [items, open]);

  const favoriteItems = useMemo(() => {
    const paths = getFavoritePaths();
    const map = new Map(items.map((i) => [i.path, i]));
    return paths.map((p) => map.get(p)).filter((x): x is NavItem => Boolean(x)).slice(0, 5);
  }, [items, open]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, [setOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, open, setOpen]);

  const go = (item: NavItem) => {
    navigate(item.path);
    close();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden min-h-[44px] rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-3 py-2 text-xs text-[var(--shell-fg-muted)] hover:bg-[var(--shell-hover)] lg:block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]"
        aria-label="Open command palette (Ctrl+K)"
      >
        Ctrl+K
      </button>
    );
  }

  const list = filtered.length ? filtered : recentItems;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div className="w-full max-w-xl rounded-[var(--shell-radius-lg)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] shadow-2xl">
        <label htmlFor="command-palette-input" className="sr-only">
          Command palette search
        </label>
        <input
          id="command-palette-input"
          autoFocus
          type="search"
          placeholder="Jump to page or action…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, Math.max(list.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && list[active]) {
              e.preventDefault();
              go(list[active]);
            }
          }}
          className="w-full border-b border-[var(--shell-border)] bg-transparent px-4 py-3 text-sm text-[var(--shell-fg)] focus:outline-none"
        />
        {!query && favoriteItems.length > 0 && (
          <div className="border-b border-[var(--shell-border)] px-4 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--shell-fg-subtle)]">
              Favorites
            </p>
            <ul className="mt-1 flex flex-wrap gap-2">
              {favoriteItems.map((item) => (
                <li key={`fav-${item.id}`}>
                  <button
                    type="button"
                    className="rounded-md border border-[var(--shell-border)] px-2 py-1 text-xs text-[var(--shell-fg)] hover:bg-[var(--shell-hover)]"
                    onClick={() => go(item)}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <ul className="max-h-80 overflow-auto py-2" role="listbox">
          {list.map((item, i) => (
            <li key={item.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm ${
                  i === active ? 'bg-[var(--shell-accent-muted)]' : 'hover:bg-[var(--shell-hover)]'
                }`}
                onClick={() => go(item)}
                onMouseEnter={() => setActive(i)}
              >
                <span>
                  <span className="font-medium text-[var(--shell-fg)]">{item.label}</span>
                  <span className="ml-2 text-xs text-[var(--shell-fg-subtle)]">{item.group}</span>
                </span>
                <span className="text-xs text-[var(--shell-fg-subtle)]">{item.path}</span>
              </button>
            </li>
          ))}
          {list.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-[var(--shell-fg-muted)]">No matches</li>
          )}
        </ul>
        <div className="flex items-center justify-between border-t border-[var(--shell-border)] px-4 py-2 text-xs text-[var(--shell-fg-subtle)]">
          <span>AI actions · coming later</span>
          <Link to="/command" onClick={close} className="text-[var(--shell-accent)] hover:underline">
            Home
          </Link>
        </div>
      </div>
      <button type="button" aria-label="Close command palette" className="fixed inset-0 -z-10" onClick={close} />
    </div>
  );
}
